// guardManager.ts
import Frontend from "tm:frontend";

type GuardParams = string[];
type ActiveGuards = Map<string, { params: GuardParams; count: number }>;


class GuardManager {
    private metaParams: Map<string, GuardParams> = new Map();
    private activeGuards: ActiveGuards = new Map();
    private currentIframe: HTMLIFrameElement | null = null;
    private _lastUpdate: Promise<void>|null = null;

    constructor() {
        this.loadMetaParams();
    }

    private loadMetaParams() {
        const metaTags = document.querySelectorAll<HTMLMetaElement>(
            'meta[name^="tm:guard:"]'
        );
        metaTags.forEach((meta) => {
            const guardName = meta.name.slice("tm:guard:".length);
            if (!this.metaParams.has(guardName)) {
                this.metaParams.set(guardName, []);
            }
            this.metaParams.get(guardName)!.push(meta.content);
        });
    }

    addGuard(name: string) {
        const params = this.metaParams.get(name) || [];
        if (this.activeGuards.has(name)) {
            this.activeGuards.get(name)!.count++;
            console.debug(`[Guard::Manager] Activating ${name}, already active (${this.activeGuards.get(name)!.count} references)`)
            return;
        } else {
            this.activeGuards.set(name, { params: [...params], count: 1 });
            console.debug(`[Guard::Manager] Activating ${name} (${this.activeGuards.get(name)!.count} references)`)
            this.updateIframe();
        }
    }

    reload() {
        this.updateIframe();
    }

    removeGuard(name: string) {
        const entry = this.activeGuards.get(name);
        if (!entry) return;
        entry.count--;
        if (entry.count <= 0) {
            this.activeGuards.delete(name);
            console.debug(`[Guard::Manager] Deactivating ${name} (Reloading iframe)`)
            this.updateIframe();
        } else {
            console.debug(`[Guard::Manager] Deactivating ${name}, keep active (${this.activeGuards.get(name)?.count || 0} references)`)
            return;
        }
    }

    private buildGuardUrl(): string {
        if (this.activeGuards.size === 0) return "";
        const port = !!window.location.port ? `:${window.location.port}` : "";
        const origin = `${window.location.protocol}//wg-${Frontend.pid}.runtime.${Frontend.base_domain}${port}`;
        const params = new URLSearchParams();
        const guardsList: string[] = [];
        for (const [name, { params: guardParams }] of this.activeGuards) {
            guardsList.push(name);
            guardParams.forEach((p) => {
                params.append(`${name}[]`, p);
            });
        }
        params.set("guards", guardsList.join(","));
        return `${origin}${window.location.pathname}?${params.toString()}`;
    }

    private updateIframe() {
        if (!this._lastUpdate) {
            this._lastUpdate = (async () => {
                try {
                    await this._updateIframe();
                } catch (e) {
                    window.reportError(e);
                } finally {
                    this._lastUpdate = null;
                }
            })();
        }
    }

    private async _updateIframe() {
        const url = this.buildGuardUrl();

        if (!url) {
            // No guards active — remove iframe
            if (this.currentIframe) {
                this.currentIframe.remove();
                this.currentIframe = null;
            }
            return;
        }

        if (this.currentIframe) {
            await new Promise(rs => requestAnimationFrame(rs));
        }

        const newIframe = document.createElement("iframe");
        newIframe.src = url;
        newIframe.style.display = "none";

        while (!document.body)
            await new Promise(rs => setTimeout(rs, 100));

        // Append new iframe first
        document.body.appendChild(newIframe);

        if (this.currentIframe) {
            const oldIframe = this.currentIframe;
            this.currentIframe = newIframe;
            // Wait for new iframe to load before removing old one
            await new Promise<void>(rs => {
                newIframe.addEventListener("load", () => {
                    oldIframe.remove();
                    rs();
                });
            });
        } else {
            this.currentIframe = newIframe;
        }
    }
}


export default new GuardManager();
